import type { DrawerProps } from '@fluentui/react-components'
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  OverlayDrawer,
  tokens,
  useRestoreFocusSource,
} from '@fluentui/react-components'
import { Dismiss20Regular } from '@fluentui/react-icons'
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useScrollLock } from '../../hooks'
import { OverlayFooterContext } from './OverlayContext'

interface OverlayProps {
  title: string
  content: ReactElement
  position: Required<DrawerProps>['position']
  isOpen: boolean
  size: number // value for vh or vw depending on the position
  setOpen: Dispatch<SetStateAction<boolean>>
}

const useStyles = makeStyles({
  overlay: {
    borderTopLeftRadius: tokens.borderRadius3XLarge,
    borderTopRightRadius: tokens.borderRadius3XLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow64,
  },
  drawerHeader: {
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL} ${tokens.spacingVerticalM} ${tokens.spacingHorizontalXXL}`,
  },
  drawerFooter: {
    '> *': { width: '100%' },
  },
  drawerBody: {
    // without this the header looses border between it and the body
    position: 'sticky',
  },
})

const Overlay = ({ title, content, position, isOpen, size, setOpen }: OverlayProps) => {
  const restoreFocusSourceAttributes = useRestoreFocusSource()
  const { t } = useTranslation()
  const classes = useStyles()

  // Footer is optional and injected by content components via useOverlayFooter.
  // OverlayFooterContext exposes setFooter so content can register/clear its own footer
  // node without the caller (e.g. Files.page.tsx) needing to know about it.
  const [footer, setFooter] = useState<ReactNode>(null)

  // do not show the scrollbar on the main content when the overlay is open
  useScrollLock(isOpen)

  const drawerStyles = {
    ...(position === 'start' || position === 'end'
      ? { width: `${size}vw` }
      : { height: `${size}vh` }),
  }

  return (
    <OverlayDrawer
      className={classes.overlay}
      position={position}
      inertTrapFocus={true}
      open={isOpen}
      style={drawerStyles}
      onOpenChange={(_, { open }) => setOpen(open)}
      {...restoreFocusSourceAttributes}>
      <DrawerHeader className={classes.drawerHeader}>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={t('close')}
              icon={<Dismiss20Regular />}
              onClick={() => setOpen(false)}
            />
          }>
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <OverlayFooterContext value={setFooter}>
        <DrawerBody className={classes.drawerBody}>{content}</DrawerBody>
        {footer && <DrawerFooter className={classes.drawerFooter}>{footer}</DrawerFooter>}
      </OverlayFooterContext>
    </OverlayDrawer>
  )
}

export { Overlay }
